import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { Credential, createCredential } from '@ukam/core/models/credential';
import { ResultService } from '@ukam/core/services/result.service';
import { CacheService } from '@ukam/core/services/cache.service';
import { HttpClient } from '@angular/common/http';
import { API_BASE_URL } from '@ukam/core/tokens/api-base-url.token';

@Injectable({
  providedIn: 'root',
})
export class CredentialService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly url = `${this.apiBaseUrl}/credentials`;

  private resultService = inject(ResultService);
  private cacheService = inject(CacheService);

  private loading$: Observable<Credential> | null = null;

  working = signal(false);
  credential = signal<Credential | null>(null);

  constructor() {
    this.cacheService.register(() => this.clear());
  }

  load(force = false): Observable<Credential> {
    if (!force && this.credential()) {
      return of(this.credential()!);
    }

    if (!force && this.loading$) {
      return this.loading$;
    }

    this.working.set(true);
    this.loading$ = this.http.get<Credential>(this.url)
      .pipe(
        tap({
          next: (result) => {
            this.credential.set(createCredential(result));
            this.working.set(false);
            this.loading$ = null;
          },
          error: (error) => {
            this.resultService.showError(error);
            this.working.set(false);
            this.loading$ = null;
          },
        }),
        shareReplay(1)
      );

    return this.loading$;
  }

  get(): Credential | null {
    return this.credential();
  }

  clear(): void {
    this.credential.set(null);
    this.loading$ = null;
  }
}
